'use client';

import { motion } from 'framer-motion';
import { Sparkles, Check, ArrowUpRight } from 'lucide-react';
import s from './CapabilityBento.module.css';

/*
 * <CapabilityBento>
 *
 * Two-row masonry layout following the Nexus reference:
 *
 *   ┌────────────────────────────┬─────────────────────┐
 *   │  Row 1 — TITLE CARD         │  POINTER CARD       │
 *   │  (left, wider — variant     │  (right, narrower — │
 *   │   pulled from .deck.titleCls) │  bullet 1)         │
 *   └────────────────────────────┴─────────────────────┘
 *   ┌──────────┬──────────┬──────────┐
 *   │  Row 2   │  Row 2   │  Row 2   │
 *   │  V_A     │  V_B     │  V_C     │
 *   │  (bullet │  (bullet │  (bullet │
 *   │   2)     │   3)     │   4)     │
 *   └──────────┴──────────┴──────────┘
 *
 * The card variants — brand-blue solid, tinted-with-chart, white-with-
 * accent-stripe, dark-with-glow, light-with-circle-cluster — are
 * deliberately mixed across tabs via the `deckIndex` so each capability
 * panel feels different even with the same underlying data shape.
 *
 * ── PROPS ─────────────────────────────────────────────────────────────
 *
 * capability: {
 *   Icon, color, title, sub, desc, bullets: string[]
 * }
 * deckIndex: number    — which preset variant deck to use (rotates).
 * onSkip?: () => void  — optional "skip this section" handler (renders
 *                        a small chip in the top-right corner of the
 *                        bento if provided).
 */

/* 4 deck presets — each picks variants for the title card, pointer
   card, and the 3 row-2 cards. Rotating across the 10 capabilities
   gives the section a deliberate, designer-mixed feel. */
import { useTranslation } from "@/hooks/useTranslation";
const DECKS = [{
  title: 'brand',
  pointer: 'whiteAccent',
  row2: ['tintChart', 'whiteAccent', 'darkGlow']
}, {
  title: 'darkGlow',
  pointer: 'tintChart',
  row2: ['whiteAccent', 'lightCircle', 'brand']
}, {
  title: 'brand',
  pointer: 'lightCircle',
  row2: ['darkGlow', 'tintChart', 'whiteAccent']
}, {
  title: 'tintChart',
  pointer: 'brand',
  row2: ['lightCircle', 'whiteAccent', 'darkGlow']
}];

/* Stagger preset — orchestrates card entrance so the bento "builds up"
   when a new tab activates: title card rises first, then pointer card,
   then the row-2 cards follow one by one. */
const BENTO_CONTAINER_VARIANTS = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05
    }
  }
};
const BENTO_CARD_VARIANTS = {
  hidden: {
    opacity: 0,
    y: 28,
    scale: 0.95,
    filter: 'blur(6px)'
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      y: {
        type: 'spring',
        damping: 16,
        stiffness: 130,
        mass: 0.9
      },
      scale: {
        type: 'spring',
        damping: 15,
        stiffness: 140,
        mass: 0.9
      },
      opacity: {
        duration: 0.32,
        ease: [0.22, 1, 0.36, 1]
      },
      filter: {
        duration: 0.32
      }
    }
  }
};
export function CapabilityBento({
  capability: c,
  deckIndex = 0,
  onSkip,
  pointerOverride,
  // optional — show this string in the pointer card instead of bullets[0]
  pointerLabel = 'Highlight' // small uppercase kicker on the pointer card
}) {
  const {
    t
  } = useTranslation();
  const deck = DECKS[deckIndex % DECKS.length];
  const Icon = c.Icon;
  const bullets = c.bullets || [];
  /* If a pointerOverride is supplied, the pointer card shows that
     custom string and ALL bullets slot into row 2. Otherwise we fall
     back to the original behaviour: pointer = bullets[0], row 2 =
     bullets[1..3]. Row 2 caps at 5 cards visually. */
  const pointerBullet = pointerOverride ?? bullets[0];
  const rest = pointerOverride ? bullets.slice(0, 5) : bullets.slice(1, 4);
  return <motion.div className={s.bento} variants={BENTO_CONTAINER_VARIANTS} initial="hidden" animate="show">
      {/* Skip-section chip (optional) */}
      {onSkip ? <button type="button" className={s.skipChip} onClick={onSkip}>
          {t("capabilitybento.skip_capabilities")} <ArrowUpRight size={13} strokeWidth={2.4} />
        </button> : null}

      {/* ── Row 1 — title + first-bullet pointer (or pointerOverride) */}
      <div className={s.row1}>
        <motion.div variants={BENTO_CARD_VARIANTS}>
          <CardTitle variant={deck.title} capability={c} Icon={Icon} />
        </motion.div>
        <motion.div variants={BENTO_CARD_VARIANTS}>
          <CardPointer variant={deck.pointer} capability={c} bullet={pointerBullet} label={pointerLabel} />
        </motion.div>
      </div>

      {/* ── Row 2 — mix of variants for the remaining bullets ──── */}
      {rest.length > 0 ? <div className={s.row2} data-count={rest.length}>
          {rest.map((bullet, i) => <motion.div key={i} variants={BENTO_CARD_VARIANTS}>
              <CardBullet variant={deck.row2[i % deck.row2.length]} bullet={bullet} capability={c} index={i} />
            </motion.div>)}
        </div> : null}
    </motion.div>;
}

/* ─── Variants ─────────────────────────────────────────────────────── */

function CardTitle({
  variant,
  capability: c,
  Icon
}) {
  const {
    t
  } = useTranslation();
  const cls = `${s.card} ${s.titleCard} ${s[`v_${variant}`]}`;
  return <article className={cls}>
      <div className={s.cardTopRow}>
        <span className={s.kicker}>
          <Sparkles size={11} strokeWidth={2.5} /> {t("capabilitybento.capability")}
        </span>
        <span className={s.cornerIcon} style={{
        background: variant === 'brand' || variant === 'darkGlow' ? 'rgba(255,255,255,0.16)' : c.colorLight,
        color: variant === 'brand' || variant === 'darkGlow' ? '#FFFFFF' : c.color
      }}>
          <Icon size={18} strokeWidth={2.2} />
        </span>
      </div>
      <h3 className={s.titleBig}>{c.title}</h3>
      <div className={s.titleSub}>{c.sub}</div>
      <p className={s.titleDesc}>{c.desc}</p>
    </article>;
}
function CardPointer({
  variant,
  capability: c,
  bullet,
  label = 'Highlight'
}) {
  const {
    t
  } = useTranslation();
  if (!bullet) return null;
  const cls = `${s.card} ${s.pointerCard} ${s[`v_${variant}`]}`;
  return <article className={cls}>
      <div className={s.cardTopRow}>
        <span className={s.kickerSm}>{label}</span>
        <span className={s.dot} style={{
        background: c.color
      }} />
      </div>
      <div className={s.pointerText}>{bullet}</div>
      {/* Decorative element — varies per variant */}
      <Decoration variant={variant} color={c.color} />
    </article>;
}
function CardBullet({
  variant,
  bullet,
  capability: c,
  index
}) {
  const {
    t
  } = useTranslation();
  const cls = `${s.card} ${s.bulletCard} ${s[`v_${variant}`]}`;
  return <article className={cls}>
      <div className={s.cardTopRow}>
        <span className={s.tickIcon} style={{
        background: variant === 'brand' || variant === 'darkGlow' ? 'rgba(255,255,255,0.18)' : c.colorLight,
        color: variant === 'brand' || variant === 'darkGlow' ? '#FFFFFF' : c.color
      }}>
          <Check size={12} strokeWidth={3} />
        </span>
        <span className={s.numChip}>0{index + 2}</span>
      </div>
      <p className={s.bulletText}>{bullet}</p>
      <Decoration variant={variant} color={c.color} />
    </article>;
}

/* ─── Per-variant decorative micro-elements (the "not boring" bit) ─── */

function Decoration({
  variant,
  color
}) {
  const {
    t
  } = useTranslation();
  if (variant === 'tintChart') {
    // Mini bar trio
    return <div className={s.deco}>
        <div className={s.barRow}>
          {[42, 62, 88, 76, 54].map((h, i) => <span key={i} className={s.bar} style={{
          height: `${h}%`,
          background: i % 2 ? color : 'rgba(15,23,42,0.18)'
        }} />)}
        </div>
      </div>;
  }
  if (variant === 'lightCircle') {
    // Cluster of overlapping circles (like agency.p)
    return <div className={`${s.deco} ${s.decoCircles}`}>
        {[0, 1, 2].map(i => <span key={i} className={s.circle} style={{
        background: i === 1 ? color : 'rgba(15,23,42,0.10)',
        marginLeft: i === 0 ? 0 : -10
      }} />)}
        <span className={s.plusChip} style={{
        background: color
      }}>+</span>
      </div>;
  }
  if (variant === 'darkGlow') {
    // Brand-coloured radial glow
    return <div className={s.deco}>
        <span className={s.glowOrb} style={{
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`
      }} />
      </div>;
  }
  if (variant === 'whiteAccent') {
    // Diagonal accent stripe
    return <div className={s.deco}>
        <span className={s.diagStripe} style={{
        background: `linear-gradient(135deg, ${color} 0%, transparent 100%)`
      }} />
      </div>;
  }
  if (variant === 'brand') {
    // Subtle white blob
    return <div className={s.deco}>
        <span className={s.blobWhite} />
      </div>;
  }
  return null;
}